import { Dispatch } from 'redux';
import { getAllAccountCategories } from 'app/shared/api/getAccountCategories';
import { getAccountCategoryById as getAccountCategoryByIdApi } from 'app/shared/api/getAccountCategoryById';
import { saveAccountCategory as saveAccountCategoryApi, SaveAccountCategoryParams } from 'app/shared/api/saveAccountCategory';

// ─── Types ─────────────────────────────────────────────────────────────────────
export type AccountCategory = {
  id: number;
  name: string;
  createdBy: string;
  lastModifiedBy: string;
  active: boolean;
};

export type SavePayload = SaveAccountCategoryParams;

// ─── Action Types ──────────────────────────────────────────────────────────────
export const FETCH_ACCOUNTS = 'ACCOUNT_MANAGER/FETCH_ACCOUNTS';
export const SET_ACCOUNTS = 'ACCOUNT_MANAGER/SET_ACCOUNTS';

export const SAVE_ACCOUNT_REQUEST = 'ACCOUNT_MANAGER/SAVE_ACCOUNT_REQUEST';
export const SAVE_ACCOUNT_SUCCESS = 'ACCOUNT_MANAGER/SAVE_ACCOUNT_SUCCESS';
export const SAVE_ACCOUNT_FAILURE = 'ACCOUNT_MANAGER/SAVE_ACCOUNT_FAILURE';

export const ACCOUNT_SET_FILTER = 'ACCOUNT_MANAGER/SET_FILTER';
export const ACCOUNT_MODAL_OPEN = 'ACCOUNT_MANAGER/MODAL_OPEN';
export const ACCOUNT_MODAL_CLOSE = 'ACCOUNT_MANAGER/MODAL_CLOSE';

// ─── Action Creators ───────────────────────────────────────────────────────────
export const setAccounts = (items: AccountCategory[]) => ({
  type: SET_ACCOUNTS,
  payload: items,
});

export const setFilter = (value: string) => ({
  type: ACCOUNT_SET_FILTER,
  payload: value,
});

export const openModal = (editing: AccountCategory | null) => ({
  type: ACCOUNT_MODAL_OPEN,
  payload: editing,
});

export const closeModal = () => ({
  type: ACCOUNT_MODAL_CLOSE,
});

// ─── Thunks ────────────────────────────────────────────────────────────────────
export const fetchAccountCategories = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_ACCOUNTS });
  try {
    const data = await getAllAccountCategories();
    dispatch(setAccounts(Array.isArray(data) ? data : []));
  } catch {
    dispatch(setAccounts([]));
  }
};

export const fetchAccountCategoryById =
  (id: number) => async (): Promise<AccountCategory | null> => {
    try {
      const dto = await getAccountCategoryByIdApi(id);
      return (dto as AccountCategory) ?? null;
    } catch {
      return null;
    }
  };

export const saveAccountCategory = (payload: SavePayload) => async (dispatch: Dispatch) => {
  dispatch({ type: SAVE_ACCOUNT_REQUEST });
  try {
    const res = await saveAccountCategoryApi(payload);
    if (!res.ok) {
      dispatch({ type: SAVE_ACCOUNT_FAILURE, error: res.message });
      return { ok: false as const, message: res.message };
    }

    dispatch({ type: SAVE_ACCOUNT_SUCCESS });
    await (fetchAccountCategories() as any)(dispatch);
    return { ok: true as const };
  } catch (error: any) {
    const message = error?.message || 'Failed to save category.';
    dispatch({ type: SAVE_ACCOUNT_FAILURE, error: message });
    return { ok: false as const, message };
  }
};